/**
 * A tab that outlived a deploy.
 *
 * Every page's code is a separate file with a content hash in its name -
 * `OrderQueuePage-BlImnLYg.js` - and the tab learns those names from the
 * `index.html` it loaded. Leave the tab open overnight, ship a build in the
 * morning, and the first page nobody had opened yet asks for a file the new
 * build no longer has. The import fails, and without this the whole screen was
 * React Router's developer error page.
 *
 * Nothing is broken except the tab's idea of which build is current, so the
 * fix is the one a person would make: reload, and pick up the new
 * `index.html`. Navigations are network-first in the service worker, so a
 * reload does get the new one whenever the line is up.
 *
 * Once, not forever: if the reload lands on the same failure (the server is
 * really missing the file, or the line is down), looping would leave the
 * counter flashing white. The second time the error screen shows instead and
 * offers the reload as a button.
 */

const KEY = 'tlp.reloadedForBuild';
/** A second failure inside this window means reloading did not help. */
const WINDOW_MS = 30_000;

/** Set the moment a reload is asked for, so nothing else draws over it. */
let reloading = false;

export function isReloadingForNewBuild(): boolean {
  return reloading;
}

/** The message each browser gives for a code file that would not load. */
const STALE_PATTERNS = [
  /Failed to fetch dynamically imported module/i, // Chrome, Edge
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari
  /Unable to preload CSS/i, // Vite, for a page's stylesheet
  /'text\/html' is not a valid JavaScript MIME type/i, // A host answering a missing file with index.html
];

export function isStaleBuildError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return STALE_PATTERNS.some((pattern) => pattern.test(message));
}

function readStamp(): number {
  try {
    return Number(sessionStorage.getItem(KEY) ?? 0);
  } catch {
    return 0;
  }
}

/**
 * Reloads to pick up the current build, unless that was already tried a
 * moment ago. Returns whether a reload is under way.
 */
export function reloadForNewBuild(): boolean {
  if (reloading) return true;
  if (Date.now() - readStamp() < WINDOW_MS) return false;
  try {
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    // No storage (a locked-down browser): reload anyway. Without the stamp a
    // persistent failure could repeat, but every reload is a fresh attempt at
    // the network, and the error screen is still there if it never works.
  }
  reloading = true;
  window.location.reload();
  return true;
}

/**
 * Vite's own signal for the same failure, raised when it preloads a page's
 * dependencies ahead of the import. Handling it here stops it reaching the
 * console as an unhandled error and reloads before the page is even asked for.
 */
export function listenForStaleBuild() {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadForNewBuild()) event.preventDefault();
  });
}
